import type { SerializedDirectedVideo } from 'fym-shared/src/music24/directedVideoApi';
import type { DirectedScreen } from '../directedSteps';

/**
 * Where every forward button of the cast step goes (owner fix 2026-10-19: the story banner said "Go
 * on to the style" while the bottom button still said "Write 3 stories"). ONE rule for the banner,
 * the foot and any other forward button:
 * - no story yet: the director writes the stories;
 * - a story: go on to the step that the video is at (`currentScreen`); the style step first gets
 *   its styles when the director did not write them yet.
 */
export type CastNext =
  | { kind: 'stories' }
  | { kind: 'goOn'; step: DirectedScreen; writeStyles: boolean; usesDirector: boolean };

/** Pure. */
export function castNextOf(
  video: Pick<SerializedDirectedVideo, 'story' | 'styles' | 'style'>,
  currentScreen: DirectedScreen
): CastNext {
  if (video.story === null) {
    return { kind: 'stories' };
  }
  // A video with a story is at the style step or later; `cast` and `stories` cannot hold a story.
  const step: DirectedScreen = currentScreen === 'cast' || currentScreen === 'stories' ? 'style' : currentScreen;
  const writeStyles = step === 'style' && video.styles === null && video.style === null;
  return { kind: 'goOn', step, writeStyles, usesDirector: writeStyles };
}

/** Why the forward buttons are off, or null. `castPending` shows its line next to the button. */
export type CastNextLock = 'readOnly' | 'busy' | 'castPending' | 'consent' | 'noRuns' | null;

/** The one lock rule of every forward button of the cast step. Pure. */
export function castNextLock(input: {
  next: CastNext;
  readOnly: boolean;
  /** The director works, or a forward action runs now. */
  isBusy: boolean;
  /** A cast member is made, goes up or is read now (`directedCastIsPending`). */
  castPending: boolean;
  consentMissing: boolean;
  directorRunsLeft: number;
}): CastNextLock {
  if (input.readOnly) {
    return 'readOnly';
  }
  if (input.isBusy) {
    return 'busy';
  }
  if (input.castPending) {
    return 'castPending';
  }
  if (input.consentMissing) {
    return 'consent';
  }
  const usesDirector = input.next.kind === 'stories' || input.next.usesDirector;
  return usesDirector && input.directorRunsLeft <= 0 ? 'noRuns' : null;
}
